import React, { useEffect, useState } from 'react';
import { UserAlias } from '../../store/user/types';
import { RadioButton } from '../ui';
import { AddButton } from '../ui/inputs/AddButton/AddButton';
import useTheme from '@material-ui/core/styles/useTheme';
import AddProfileEmailDialog from '../dialogs/AddProfileEmailDialog/AddProfileEmailDialog';
import TrashCan from '../../icons/TrashCan';
import SimpleDialog from '../dialogs/SimpleDialog/SimpleDialog';
import { connect } from 'react-redux';
import userSlice from '../../store/user/slice';
import { AppDispatch } from '../../store/store';
import { AppState } from '../../store/state';
import { useTranslation, Trans } from 'react-i18next';

export interface VerifyCode {
  email: string;
  code: string;
}

export interface MainEmailFormProps {
  user_alias_by_id: { [key: number]: UserAlias };
  main_user_id: number;
  save_handler: (user_id: number) => void;
  deleteUserAliasAction: (userAliasId: number) => void;
}

const MainEmailForm: React.FC<MainEmailFormProps> = ({
  user_alias_by_id,
  main_user_id,
  save_handler,
  deleteUserAliasAction,
}) => {
  const { t, i18n } = useTranslation();

  const [checked, setChecked] = React.useState<number>(main_user_id);
  const [dialog, setDialog] = useState<JSX.Element | undefined>(undefined);

  const theme = useTheme();

  useEffect(() => {
    setChecked(main_user_id);
  }, [user_alias_by_id]);

  // Function to handle the delete email address dialog
  const handleDeleteAlias = (userAlias: UserAlias) => {
    const email = userAlias.email;
    setDialog(
      <SimpleDialog
        open={true}
        handleClose={() => setDialog(undefined)}
        handleOk={() => {
          deleteUserAliasAction(userAlias.id);
        }}
        title={t('Remove email address?')}
      >
        <p className="text-sm mb-2">
          <Trans i18nKey="emailRemoveWarning">
            Are you sure you want to remove the address <span className="font-bold">{{ email }}</span>? Take into
            account that this action can't be undone.
          </Trans>
        </p>
      </SimpleDialog>,
    );
  };

  const listItems = Object.values(user_alias_by_id).map((userAlias, index) => {
    const { id: value, email, email_verified_at } = userAlias;

    return (
      <div key={index} className={`mb-4 w-max relative flex items-center ${!email_verified_at ? 'itemDisabled' : ''}`}>
        <RadioButton
          name="options"
          label={email}
          checked={value === checked}
          value={value}
          onChange={(e) => {
            const userId = parseInt(e.target.value);
            setChecked(userId);
            save_handler(userId);
          }}
          disabled={!email_verified_at}
        />
        {value === main_user_id && (
          <div
            className="rounded-full text-xs text-white px-1.5 py-0.5"
            style={{
              backgroundColor: `${theme.palette.primary.main}`,
            }}
          >
            {t('Main')}
          </div>
        )}
        {value !== main_user_id && (
          <div className="ml-2 cursor-pointer" onClick={() => handleDeleteAlias(userAlias)}>
            <TrashCan />
          </div>
        )}
      </div>
    );
  });

  return (
    <>
      <div className="flex flex-col">
        <div className="mb-6">
          <p className="text-xl font-medium mb-1">{t('Email')}</p>
          <p className="text-sm">{t('Manage your access email')}</p>
        </div>
        <div>{listItems}</div>
        <AddButton
          label={t('Add email')}
          onClick={() => {
            setDialog(<AddProfileEmailDialog handleClose={() => setDialog(undefined)} />);
          }}
        />
      </div>
      {dialog}
    </>
  );
};

const mapStateToProps = (state: AppState) => ({});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  deleteUserAliasAction: (userAliasId: number) => dispatch(userSlice.actions['DELETE:ALIAS'](userAliasId)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MainEmailForm);
